import { storage } from '@uhg-abyss/web/tools/storage';
import { createTheme } from '@uhg-abyss/web/tools/theme';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { I18nProvider } from '@uhg-abyss/web/ui/I18nProvider';
import { ThemeProvider } from '@uhg-abyss/web/ui/ThemeProvider';
import find from 'lodash/find';
import { useCallback, useState } from 'react';

import { useGeoLocationStorage } from '../..//hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { ProviderLocation } from '../../models/ProviderDetails';
import { Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { ContentWrapper } from '../ContentWrapper/ContentWrapper';
import { ContainerForAllSectionsStyled } from '../ProviderDetailTabs/ProviderDetailTabs.styled';
import { getFeatureFlag } from '../Utils';
import { OutOfStateBannerWrapper } from './OutOfStateBanner.style';

interface OutOfStateBannerProps {
  providersData?: any[];
  selectedLocation?: ProviderLocation;
}

export const OutOfStateBanner = ({
  providersData,
  selectedLocation,
}: OutOfStateBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const { stateCode } = useGeoLocationStorage();

  const isOutOfStateVirtualProviderExist = useCallback(() => {
    if (providersData && providersData.length > 0) {
      return providersData.some(
        (provider) => provider?.address.state !== stateCode
      );
    } else if (selectedLocation) {
      return selectedLocation?.address.state !== stateCode;
    }
    return false;
  }, [providersData, stateCode, selectedLocation]);
  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();

  const outOfStateDescription = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.OUT_OF_STATE_BANNER_DESCRIPTION,
  });

  const isOutOfStateVirtualProviderEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_OUT_OF_STATE_VIRTUAL_PROVIDERS
  );

  const isOutOfStateBannerCanceled =
    storage.session.get(
      Constants.STORAGE_KEYS.SESSION.OUT_OF_STATE_BANNER_CANCELED
    ) ?? false;

  const closeBanner = () => {
    setIsVisible(false);
    storage.session.set(
      Constants.STORAGE_KEYS.SESSION.OUT_OF_STATE_BANNER_CANCELED,
      true
    );
  };

  const showOutOfStateBanner =
    isOutOfStateVirtualProviderEnabled &&
    isVisible &&
    isOutOfStateVirtualProviderExist() &&
    !isOutOfStateBannerCanceled;

  const theme = createTheme('uhc');

  const Banner = (
    <ThemeProvider theme={theme}>
      <I18nProvider translations={{ close: 'Close message' }}>
        <Alert
          css={!selectedLocation ? OutOfStateBannerWrapper : {}}
          data-testid="out-of-state-banner"
          isVisible={isVisible}
          onClose={closeBanner}
          status="info"
          title={outOfStateDescription?.title}
        >
          {outOfStateDescription?.message}
        </Alert>
      </I18nProvider>
    </ThemeProvider>
  );

  return (
    showOutOfStateBanner &&
    (selectedLocation ? (
      <ContainerForAllSectionsStyled css={{ marginBottom: '8px' }}>
        <ContentWrapper>{Banner}</ContentWrapper>
      </ContainerForAllSectionsStyled>
    ) : (
      Banner
    ))
  );
};
